import type { Schemas } from "@acervo/api-client";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Play } from "lucide-react";
import { Cover, Empty, ErrorBox, ProgressBar, Spinner, VisibilityBadge } from "../components/ui";
import { api, isAdmin, useSession } from "../lib/api";
import { clock, duration, percent } from "../lib/format";

export const homeQuery = {
  queryKey: ["home"],
  queryFn: () => api.unwrap(api.client.GET("/v1/home")),
};

export function HomePage() {
  const session = useSession();
  const q = useQuery(homeQuery);
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} onRetry={() => void q.refetch()} />;

  const { continue_watching, courses } = q.data;
  const first = session?.user.display_name.split(" ")[0];

  return (
    <div className="space-y-10">
      <h1 className="text-2xl font-bold sm:text-3xl">{first ? `Olá, ${first}` : "Olá"}</h1>

      {continue_watching.length > 0 && (
        <section aria-labelledby="continuar">
          <h2 id="continuar" className="mb-3 text-lg font-bold">
            Continuar assistindo
          </h2>
          <div className="-mx-4 flex snap-x gap-4 overflow-x-auto px-4 pb-2">
            {continue_watching.map((c) => (
              <ContinueCard key={c.lesson_id} item={c} />
            ))}
          </div>
        </section>
      )}

      <section aria-labelledby="cursos">
        <h2 id="cursos" className="mb-3 text-lg font-bold">
          Cursos
        </h2>
        {courses.length === 0 ? (
          <Empty title="Nenhum curso por aqui ainda">
            {isAdmin(session)
              ? "Envie as aulas com o acervo-ingest; elas aparecem aqui depois da importação (Console → Cursos)."
              : "Quando houver cursos disponíveis para você, eles aparecem aqui."}
          </Empty>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {courses.map((c) => (
              <CourseTile key={c.id} course={c} showVisibility={isAdmin(session)} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function ContinueCard({ item }: { item: Schemas["ContinueItem"] }) {
  return (
    <Link
      to="/aula/$lessonId"
      params={{ lessonId: item.lesson_id }}
      className="group card w-72 shrink-0 snap-start overflow-hidden hover:border-accent/60"
    >
      <div className="relative aspect-video bg-raised">
        <Cover title={item.course_title} url={item.poster_url} className="h-full w-full" />
        <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 transition-opacity group-hover:opacity-100">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent text-bg">
            <Play className="h-5 w-5 fill-current" />
          </span>
        </div>
        <span className="absolute right-2 bottom-2 rounded bg-black/70 px-1.5 py-0.5 font-mono text-[11px]">
          {clock(item.position_s)} / {clock(item.duration_s)}
        </span>
      </div>
      <ProgressBar value={percent(item.position_s, item.duration_s)} className="rounded-none" />
      <div className="p-3">
        <p className="truncate text-sm font-semibold">{item.lesson_title}</p>
        <p className="truncate text-xs text-muted">{item.course_title}</p>
      </div>
    </Link>
  );
}

export function CourseTile({ course, showVisibility }: { course: Schemas["CourseCard"]; showVisibility: boolean }) {
  return (
    <Link
      to="/cursos/$slug"
      params={{ slug: course.slug }}
      className="group card overflow-hidden transition-colors hover:border-accent/60"
    >
      <Cover title={course.title} url={course.cover_url} className="aspect-[4/3] w-full" />
      <div className="space-y-1 p-3">
        <p className="line-clamp-2 text-sm leading-snug font-semibold group-hover:text-accent">{course.title}</p>
        <p className="text-xs text-muted">
          {course.lessons} {course.lessons === 1 ? "aula" : "aulas"} · {duration(course.duration_s)}
        </p>
        {showVisibility && <VisibilityBadge course={course} />}
      </div>
    </Link>
  );
}
